// Iter309 · C11 — Solvencias y conocimiento aprobados
// Iter396 · C09 — el conocimiento llega al flujo:
//  - distingue "certificado propio" (capacidad de Valoriza) de "requisito del
//    pliego" (lo que piden en el mercado) para que nunca se confundan;
//  - alta rápida con plantillas de certificados habituales;
//  - importación de los requisitos del Mercado Sectorial como BORRADORES.
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Loader2, FileCheck2, Trash2, Pencil, ShieldAlert, Beaker, DownloadCloud, FileText } from "lucide-react";

const TIPO_META = {
  certificado:            { label: "Certificado",           cls: "bg-blue-100 text-blue-700" },
  referencia:             { label: "Referencia",            cls: "bg-purple-100 text-purple-700" },
  respuesta_reutilizable: { label: "Respuesta reutilizable", cls: "bg-emerald-100 text-emerald-700" },
  requisito:              { label: "Requisito",             cls: "bg-slate-100 text-slate-600" },
};

const CLASE_META = {
  propio:    { label: "Certificado propio",    cls: "bg-emerald-600 text-white" },
  requisito: { label: "Requisito del pliego",  cls: "bg-slate-500 text-white" },
};

// Plantillas de alta rápida (capacidades propias habituales)
const PRESETS = [
  { titulo: "ISO 9001:2015 — Gestión de la calidad",     categoria: "iso calidad",      palabras_clave: "iso 9001 calidad gestión sistema" },
  { titulo: "ISO 14001:2015 — Gestión ambiental",         categoria: "iso ambiental",    palabras_clave: "iso 14001 ambiental medio ambiente" },
  { titulo: "ISO 45001 — Seguridad y salud laboral",      categoria: "iso prl",          palabras_clave: "iso 45001 seguridad salud prevención prl" },
  { titulo: "Clasificación empresarial",                  categoria: "clasificacion",    palabras_clave: "clasificación grupo subgrupo categoría contratista" },
  { titulo: "Experiencia en servicios similares",         categoria: "experiencia",      palabras_clave: "experiencia servicios similares relación trabajos ejecutados principales" },
  { titulo: "Solvencia económica y financiera",           categoria: "economica",        palabras_clave: "solvencia económica financiera volumen anual cifra negocios seguro" },
];

const EMPTY = { titulo: "", tipo: "certificado", clase: "propio", estado: "aprobado", categoria: "", palabras_clave: "", propietario: "", fecha_emision: "", fecha_caducidad: "", es_piloto: false, evidencia_url: "", alcance_permitido: "", observaciones: "" };

export default function SolvenciasCatalogo() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filterClase, setFilterClase] = useState("todas");

  const load = () => axios.get(`${API}/ofertas-comercial/solvencias/catalogo`).then(r => setItems(r.data || [])).catch(() => toast.error("Error cargando catálogo"));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const l = items || [];
    return { todas: l.length, propio: l.filter(i => (i.clase || "propio") !== "requisito").length, requisito: l.filter(i => i.clase === "requisito").length };
  }, [items]);

  const filtered = useMemo(() => {
    const l = items || [];
    if (filterClase === "todas") return l;
    if (filterClase === "propio") return l.filter(i => (i.clase || "propio") !== "requisito");
    return l.filter(i => i.clase === "requisito");
  }, [items, filterClase]);

  const openNew = (preset) => { setForm({ ...EMPTY, ...(preset || {}) }); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it }); setEditing(it.id); };

  const save = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    if (form.clase === "propio" && !form.propietario.trim()) { toast.error("El propietario es obligatorio en una capacidad propia"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/ofertas-comercial/solvencias/catalogo`, form);
        toast.success("Solvencia añadida al catálogo");
      } else {
        await axios.put(`${API}/ofertas-comercial/solvencias/catalogo/${editing}`, form);
        toast.success("Solvencia actualizada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta solvencia del catálogo? Las referencias desde matrices de cumplimiento quedarán huérfanas.")) return;
    try {
      await axios.delete(`${API}/ofertas-comercial/solvencias/catalogo/${id}`);
      toast.success("Eliminada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const importSectorial = async () => {
    if (!window.confirm("Importa los requisitos de solvencia vistos en el Mercado Sectorial como BORRADORES de clase «requisito» para revisar. No se marcan como capacidades propias de Valoriza. ¿Continuar?")) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/ofertas-comercial/solvencias/importar-sectorial`, { limit: 500 });
      toast.success(`${data.importados} requisitos importados como borrador (${data.omitidos_ya_existian} ya existían)`);
      setFilterClase("requisito");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setImporting(false); }
  };

  if (!items) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={14}/> Cargando…</div>;

  const FILTERS = [["todas", "Todas", counts.todas], ["propio", "Propias", counts.propio], ["requisito", "Requisitos", counts.requisito]];

  return (
    <div className="space-y-4" data-testid="solvencias-catalogo">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><FileCheck2 size={16} className="text-[#007934]"/> Solvencias y conocimiento aprobados</h2>
          <p className="text-[11px] text-gray-500 mt-0.5 max-w-2xl">
            <b className="text-emerald-700">Certificados propios</b> = capacidades reales de Valoriza (vigencia y propietario).
            <b className="text-slate-600"> Requisitos del pliego</b> = lo que piden en el mercado (importados como borrador, NO son capacidad propia).
            Un piloto no acredita capacidad general; lo caducado no se usa como prueba.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={importSectorial} disabled={importing} data-testid="solvencia-import-btn"
            className="border border-slate-300 text-slate-600 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-40">
            {importing ? <Loader2 className="animate-spin" size={14}/> : <DownloadCloud size={14}/>} Importar del Mercado Sectorial
          </button>
          <button onClick={() => openNew()} data-testid="solvencia-new-btn"
            className="bg-[#007934] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#006528] flex items-center gap-1.5">
            <Plus size={14}/> Nueva solvencia
          </button>
        </div>
      </div>

      {/* Alta rápida con plantillas */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase font-semibold text-gray-400">Alta rápida:</span>
        {PRESETS.map((p, i) => (
          <button key={i} onClick={() => openNew(p)} data-testid={`solvencia-preset-${i}`}
            className="text-[11px] border border-gray-200 rounded-full px-2.5 py-1 hover:border-[#007934] hover:text-[#007934] text-gray-600 flex items-center gap-1">
            <Plus size={10}/> {p.titulo.split(" — ")[0]}
          </button>
        ))}
      </div>

      {/* Filtro por clase */}
      <div className="flex items-center gap-1.5">
        {FILTERS.map(([k, label, n]) => (
          <button key={k} onClick={() => setFilterClase(k)} data-testid={`solvencia-filter-${k}`}
            className={`text-xs font-medium rounded-lg px-3 py-1.5 border transition-colors ${filterClase === k ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
            {label} <span className="opacity-70">({n})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-10 bg-white rounded-xl border border-gray-200">
          {filterClase === "requisito" ? "Sin requisitos importados. Usa «Importar del Mercado Sectorial»." : "Sin certificados propios todavía. Usa «Alta rápida» o «Nueva solvencia»."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(it => {
            const clase = it.clase || "propio";
            const esReq = clase === "requisito";
            return (
            <div key={it.id} className={`bg-white rounded-xl border p-4 space-y-2 ${esReq ? "border-slate-200" : !it.vigente ? "border-rose-300" : it.es_piloto ? "border-amber-300" : "border-gray-200"}`} data-testid={`solvencia-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${CLASE_META[clase]?.cls}`} data-testid={`solvencia-clase-${it.id}`}>{CLASE_META[clase]?.label}</span>
                    {it.estado === "borrador" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-900" data-testid={`solvencia-borrador-${it.id}`}>BORRADOR</span>}
                    {!esReq && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${TIPO_META[it.tipo]?.cls}`}>{TIPO_META[it.tipo]?.label}</span>}
                    {!esReq && it.es_piloto && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white" title="Capacidad de PoC — no producción" data-testid={`solvencia-piloto-${it.id}`}><Beaker size={9}/> PILOTO</span>
                    )}
                    {!esReq && !it.vigente && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white" title="Caducada — no usar como prueba" data-testid={`solvencia-caducada-${it.id}`}><ShieldAlert size={9}/> CADUCADA</span>
                    )}
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mt-1.5 line-clamp-2">{it.titulo}</h3>
                  {esReq ? (
                    <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1"><FileText size={11}/> Visto en: <b className="truncate">{it.origen_cliente || "mercado"}</b></p>
                  ) : (
                    <p className="text-[11px] text-gray-500 mt-0.5">Propietario: <b>{it.propietario}</b></p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(it)} data-testid={`solvencia-edit-${it.id}`} className="text-gray-400 hover:text-[#007934] p-1"><Pencil size={13}/></button>
                  <button onClick={() => remove(it.id)} data-testid={`solvencia-delete-${it.id}`} className="text-gray-400 hover:text-rose-600 p-1"><Trash2 size={13}/></button>
                </div>
              </div>
              {it.categoria && <span className="inline-block text-[10px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">{it.categoria}</span>}
              {!esReq && it.fecha_caducidad && <p className="text-[11px] text-gray-500">Caduca: <b className={!it.vigente ? "text-rose-600" : "text-gray-700"}>{it.fecha_caducidad}</b></p>}
              {it.alcance_permitido && <p className="text-[11px] text-gray-600 bg-gray-50 rounded px-2 py-1">{it.alcance_permitido}</p>}
              {esReq && it.observaciones && <p className="text-[11px] text-gray-500 line-clamp-3 bg-slate-50 rounded px-2 py-1">{it.observaciones}</p>}
              {it.evidencia_url && <a href={it.evidencia_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline block truncate">{it.evidencia_url}</a>}
            </div>
          );})}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)} data-testid="solvencia-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-5 space-y-3 my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">{editing === "new" ? "Nueva solvencia" : "Editar solvencia"}</h3>
              <button onClick={() => setEditing(null)}><X size={18} className="text-gray-400"/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Clase</label>
                <select value={form.clase} onChange={e => { const clase = e.target.value; setForm(f => ({ ...f, clase, tipo: clase === "propio" && f.tipo === "requisito" ? "certificado" : f.tipo })); }} data-testid="solvencia-clase-select" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="propio">Certificado propio</option>
                  <option value="requisito">Requisito del pliego</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Estado</label>
                <select value={form.estado} onChange={e => setForm({...form, estado: e.target.value})} data-testid="solvencia-estado-select" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="aprobado">Aprobado</option>
                  <option value="borrador">Borrador</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Título *</label>
                <input value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} data-testid="solvencia-titulo-input" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
              </div>
              {form.clase === "propio" && (
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Tipo</label>
                  <select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} data-testid="solvencia-tipo-select" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                    {["certificado", "referencia", "respuesta_reutilizable"].map(k => <option key={k} value={k}>{TIPO_META[k].label}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Categoría (para matching)</label>
                <input value={form.categoria || ""} onChange={e => setForm({...form, categoria: e.target.value})} placeholder="iso, experiencia, económica…" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
              </div>
              {form.clase === "propio" && (
                <div className="col-span-2">
                  <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Propietario *</label>
                  <input value={form.propietario} onChange={e => setForm({...form, propietario: e.target.value})} data-testid="solvencia-propietario-input" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
                </div>
              )}
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Palabras clave (mejoran la sugerencia en ofertas)</label>
                <input value={form.palabras_clave || ""} onChange={e => setForm({...form, palabras_clave: e.target.value})} placeholder="términos que aparecen en los pliegos" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
              </div>
              {form.clase === "propio" && <>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Fecha emisión</label>
                  <input type="date" value={form.fecha_emision || ""} onChange={e => setForm({...form, fecha_emision: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Fecha caducidad</label>
                  <input type="date" value={form.fecha_caducidad || ""} onChange={e => setForm({...form, fecha_caducidad: e.target.value})} data-testid="solvencia-caducidad-input" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Alcance permitido</label>
                  <input value={form.alcance_permitido || ""} onChange={e => setForm({...form, alcance_permitido: e.target.value})} placeholder="Ej: sólo licitaciones de Servicios Urbanos en España" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
                </div>
              </>}
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">{form.clase === "requisito" ? "Texto del requisito" : "Evidencia (URL documento)"}</label>
                {form.clase === "requisito"
                  ? <textarea value={form.observaciones || ""} onChange={e => setForm({...form, observaciones: e.target.value})} rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
                  : <input value={form.evidencia_url || ""} onChange={e => setForm({...form, evidencia_url: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"/>}
              </div>
              {form.clase === "propio" && (
                <label className="col-span-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.es_piloto} onChange={e => setForm({...form, es_piloto: e.target.checked})} data-testid="solvencia-piloto-checkbox"/>
                  Es una capacidad de PoC/piloto, no una capacidad general en producción
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="text-xs border border-gray-200 px-3 py-1.5 rounded-lg">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="solvencia-save-btn" className="text-xs bg-[#007934] text-white px-4 py-1.5 rounded-lg hover:bg-[#006528] disabled:opacity-40 flex items-center gap-1.5">
                {saving && <Loader2 className="animate-spin" size={12}/>} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
